import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Flame, GraduationCap, ChevronDown, Shield, Menu, X } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [practiceOpen, setPracticeOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const practiceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (practiceRef.current && !practiceRef.current.contains(e.target)) setPracticeOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const linkClass = ({ isActive }) =>
    `text-sm px-3 py-1.5 rounded-full transition-colors ${
      isActive ? "bg-gradient-to-r from-pink-300 to-orange-300 text-slate-900 font-semibold" : "text-zinc-400 hover:text-yellow-300"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block w-full text-left px-4 py-3 rounded-lg text-base transition-colors ${
      isActive
        ? "bg-gradient-to-r from-pink-300 to-orange-300 text-slate-900 font-semibold"
        : "text-zinc-200 hover:bg-white/[0.04] hover:text-yellow-300"
    }`;

  const practiceActive = ["/quiz", "/mental"].some((p) => loc.pathname.startsWith(p));

  return (
    <div className="App min-h-screen">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-pink-300 to-orange-300 text-slate-900 flex items-center justify-center font-display font-bold text-lg shrink-0">g</div>
            <div className="leading-tight min-w-0">
              <div className="font-display text-lg font-bold tracking-tight truncate">Gyaan Maths</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate">CBSE · Class 6-12</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            <NavLink to="/classes" className={linkClass} data-testid="nav-classes">Classes</NavLink>
            <NavLink to="/learn" className={linkClass} data-testid="nav-learn">Learn</NavLink>
            <NavLink to="/topics" className={linkClass} data-testid="nav-topics">Topics</NavLink>

            <div className="relative" ref={practiceRef}>
              <button
                type="button"
                data-testid="nav-practice-trigger"
                onClick={() => setPracticeOpen((v) => !v)}
                className={`text-sm px-3 py-1.5 rounded-full transition-colors flex items-center gap-1 ${
                  practiceActive
                    ? "bg-gradient-to-r from-pink-300 to-orange-300 text-slate-900 font-semibold"
                    : "text-zinc-400 hover:text-yellow-300"
                }`}
              >
                Practice
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${practiceOpen ? "rotate-180" : ""}`} />
              </button>
              {practiceOpen && (
                <div
                  data-testid="nav-practice-menu"
                  className="absolute left-0 mt-2 w-56 rounded-lg border border-white/10 bg-[#0d1730] shadow-xl overflow-hidden"
                >
                  <NavLink
                    to="/quiz"
                    onClick={() => setPracticeOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-sm border-b border-white/5 transition-colors ${
                        isActive ? "text-yellow-300 bg-white/[0.03]" : "text-zinc-300 hover:text-yellow-300 hover:bg-white/[0.03]"
                      }`
                    }
                    data-testid="nav-quiz"
                  >
                    <div className="font-medium">Daily Quiz</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">2 slots · 5 questions</div>
                  </NavLink>
                  <NavLink
                    to="/mental"
                    onClick={() => setPracticeOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-sm transition-colors ${
                        isActive ? "text-yellow-300 bg-white/[0.03]" : "text-zinc-300 hover:text-yellow-300 hover:bg-white/[0.03]"
                      }`
                    }
                    data-testid="nav-mental"
                  >
                    <div className="font-medium">Mental Maths</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">60-second speed sprint</div>
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink to="/formulas" className={linkClass} data-testid="nav-formulas">Formulas</NavLink>
            <NavLink to="/tutor" className={linkClass} data-testid="nav-tutor">AI Tutor</NavLink>
            {user && <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">Dashboard</NavLink>}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400" data-testid="user-badge">
                  <span
                    className="flex items-center gap-1 cursor-help"
                    title={`${user.streak ?? 0}-day streak`}
                    data-testid="streak-badge"
                  >
                    <Flame className="w-3.5 h-3.5 text-orange-400" />
                    <span className="gradient-streak font-semibold">{user.streak ?? 0}</span>
                  </span>
                  <span className="text-zinc-600">·</span>
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full border border-white/10" />
                  ) : null}
                  <span>{user.name || user.email}</span>
                </div>
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    title="Admin console"
                    className={`hidden sm:inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 rounded-full border transition-colors ${
                      loc.pathname.startsWith("/admin")
                        ? "border-orange-300 text-orange-300 bg-orange-300/10"
                        : "border-white/10 text-zinc-500 hover:text-orange-300 hover:border-orange-300/50"
                    }`}
                  >
                    <Shield className="w-3 h-3" />
                    Admin
                  </Link>
                )}
                <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }} className="hidden md:inline-flex text-zinc-400 hover:text-yellow-300">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                data-testid="header-google-btn"
                size="sm"
                onClick={loginWithGoogle}
                className="hidden md:inline-flex bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold"
              >
                Sign in with Google
              </Button>
            )}

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-zinc-300 hover:text-yellow-300 hover:bg-white/[0.05] transition-colors"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-trigger"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div
            className="md:hidden border-t border-white/10 bg-[#0b132e]/98 backdrop-blur-sm max-h-[calc(100vh-64px)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-1">
              {/* User strip (if signed in) */}
              {user && (
                <div className="flex items-center gap-3 px-2 py-3 mb-2 rounded-lg bg-white/[0.03] border border-white/5">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/10" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 grid place-items-center text-sm font-bold text-slate-900">
                      {(user.name || user.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-100 truncate">{user.name || user.email}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-400" />
                      {user.streak ?? 0}-day streak
                    </div>
                  </div>
                </div>
              )}

              <NavLink to="/classes" className={mobileLinkClass} data-testid="mobile-nav-classes">Classes</NavLink>
              <NavLink to="/learn" className={mobileLinkClass} data-testid="mobile-nav-learn">Learn</NavLink>
              <NavLink to="/topics" className={mobileLinkClass} data-testid="mobile-nav-topics">Topics</NavLink>
              <NavLink to="/quiz" className={mobileLinkClass} data-testid="mobile-nav-quiz">Daily Quiz</NavLink>
              <NavLink to="/mental" className={mobileLinkClass} data-testid="mobile-nav-mental">Mental Maths</NavLink>
              <NavLink to="/formulas" className={mobileLinkClass} data-testid="mobile-nav-formulas">Formulas</NavLink>
              <NavLink to="/tutor" className={mobileLinkClass} data-testid="mobile-nav-tutor">AI Tutor</NavLink>
              {user && <NavLink to="/dashboard" className={mobileLinkClass} data-testid="mobile-nav-dashboard">Dashboard</NavLink>}
              {user?.is_admin && (
                <NavLink to="/admin" className={mobileLinkClass} data-testid="mobile-nav-admin">
                  <span className="inline-flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-300" /> Admin
                  </span>
                </NavLink>
              )}

              <div className="pt-3 mt-3 border-t border-white/5">
                {user ? (
                  <Button
                    data-testid="mobile-logout-btn"
                    variant="ghost"
                    onClick={async () => { setMobileOpen(false); await logout(); nav("/"); }}
                    className="w-full justify-start text-zinc-300 hover:text-yellow-300"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </Button>
                ) : (
                  <Button
                    data-testid="mobile-google-btn"
                    onClick={() => { setMobileOpen(false); loginWithGoogle(); }}
                    className="w-full bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold"
                  >
                    Sign in with Google
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      <footer className="border-t border-white/5 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            <span>Gyaan Maths — CBSE Class 6-12, built for board-toppers.</span>
          </div>
          <div className="font-mono">v1.1 · Sept 2026</div>
        </div>
      </footer>
    </div>
  );
}
